import kbData from '../../../data/epublisherKB.json';

/* -------------------------------------------------------------------------- */
/* Constants                                                                   */
/* -------------------------------------------------------------------------- */

export const KB_HOME_PATH = '/epublisher-kb';
export const SITE_TITLE = 'ePublisher Knowledge Base';
export const SITE_TAGLINE = 'School Newsletters, eSign Digital Forms, eFlash Messages, Surveys, & Calendars';
export const SUPPORT_PHONE = '1800 245 077';

// Static assets (header image, logos, fonts, downloaded images & PDFs) live in client/public/epublisher-kb-files (not named like the route, so the SPA fallback is not shadowed)
const PUBLIC_BASE = process.env.PUBLIC_URL || '';
export const asset = (path) => `${PUBLIC_BASE}/epublisher-kb-files/${path}`;

export const HOME_PAGE_ID = 1141;
export const FAQ_PAGE_ID = 3370;

export const kbUrl = {
  home: () => KB_HOME_PATH,
  article: (slug) => `${KB_HOME_PATH}?article=${encodeURIComponent(slug)}`,
  category: (slug) => `${KB_HOME_PATH}?category=${encodeURIComponent(slug)}`,
  page: (id) => `${KB_HOME_PATH}?page=${id}`,
  faq: (slug) => `${KB_HOME_PATH}?faq=${encodeURIComponent(slug)}`,
  archive: () => `${KB_HOME_PATH}?kb`,
  search: (q) => `${KB_HOME_PATH}?q=${encodeURIComponent(q)}`
};

/** Top menu / sidebar menu (same entries as the original site). */
export const MENU_ITEMS = [
  { label: 'Administrators', to: kbUrl.page(1143) },
  { label: 'Power Users', to: kbUrl.page(2756) },
  { label: 'Users', to: kbUrl.page(2796) },
  { label: 'Frequently Asked Questions', to: kbUrl.page(FAQ_PAGE_ID) }
];

/* -------------------------------------------------------------------------- */
/* Data (static, bundled - no CMS / network dependency)                        */
/* -------------------------------------------------------------------------- */

const { categories, articles, faqCategories, faqs, pages } = kbData;

export { faqCategories };

const collator = new Intl.Collator('en', { sensitivity: 'base', numeric: true });
const byName = (a, b) => collator.compare(a.name, b.name);
const byTitle = (a, b) => collator.compare(a.title, b.title);

export const catBySlug = {};
export const childrenOf = {};
categories.forEach((c) => {
  catBySlug[c.slug] = c;
});
categories.forEach((c) => {
  (childrenOf[c.parent || ''] = childrenOf[c.parent || ''] || []).push(c);
});
Object.values(childrenOf).forEach((list) => list.sort(byName));
export const rootCategories = childrenOf[''] || [];

/** Category chain from the top level down to `slug` (inclusive). */
export const categoryChain = (slug) => {
  const chain = [];
  let current = catBySlug[slug];
  while (current) {
    chain.unshift(current);
    current = current.parent ? catBySlug[current.parent] : null;
  }
  return chain;
};

const stripTags = (html) => {
  if (typeof DOMParser === 'undefined') return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.body.textContent || '').replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
};

/** First `n` words, like the WordPress excerpt on the original FAQ page. */
const excerptOf = (text, n = 30) => {
  const words = text.split(/\s+/);
  return words.length > n ? `${words.slice(0, n).join(' ')}` : text;
};

/** WordPress "texturize" for titles: "A - B" becomes "A – B", straight quotes become curly quotes. */
export const texturize = (s) =>
  String(s || '')
    .replace(/(^|[\s([{"“-])'/g, '$1‘')
    .replace(/'/g, '’')
    .replace(/(^|[\s([{‘-])"/g, '$1“')
    .replace(/"/g, '”')
    .replace(/\s--\s/g, ' — ')
    .replace(/\s-\s/g, ' – ');

// Articles: newest first (this is the order the original uses for Previous / Next)
export const articleList = articles
  .map((a) => ({ ...a, kind: 'article', displayTitle: texturize(a.title), text: stripTags(a.html) }))
  .sort((a, b) => b.date.localeCompare(a.date));
export const articleBySlug = {};
articleList.forEach((a, i) => {
  articleBySlug[a.slug] = a;
  a.index = i;
});
/** Neighbouring articles of `slug` (matches the live site's Previous / Next). */
export const neighbours = (slug) => {
  const a = articleBySlug[slug];
  return a ? { previous: articleList[a.index - 1] || null, next: articleList[a.index + 1] || null } : {};
};

// FAQs: same order as the original (newest first)
export const faqList = faqs
  .map((f) => {
    const text = stripTags(f.html);
    return { ...f, kind: 'faq', displayTitle: texturize(f.title), text, excerpt: excerptOf(text) };
  })
  .sort((a, b) => b.date.localeCompare(a.date));
export const faqBySlug = {};
faqList.forEach((f) => {
  faqBySlug[f.slug] = f;
});

/** Articles assigned directly to a category (the original does not roll sub-category articles up). */
export const directArticles = {};
categories.forEach((c) => {
  directArticles[c.slug] = articleList.filter((a) => a.categories.includes(c.slug)).sort(byTitle);
});

// Like the original (WordPress hides empty terms), categories with no articles anywhere beneath them are not listed
const hasContent = (slug) => directArticles[slug].length > 0 || (childrenOf[slug] || []).some((c) => hasContent(c.slug));
Object.keys(childrenOf).forEach((key) => {
  childrenOf[key] = childrenOf[key].filter((c) => hasContent(c.slug));
});
rootCategories.splice(0, rootCategories.length, ...(childrenOf[''] || []));

/** The category the original shows in an article's breadcrumb: the first one, by name. */
export const primaryCategory = (article) =>
  [...article.categories].sort((a, b) => collator.compare(catBySlug[a].name, catBySlug[b].name))[0];

// Elementor pages
export const pageById = {};
pages.forEach((p) => {
  pageById[p.id] = p;
});
export const pageChain = (id) => {
  const chain = [];
  let current = pageById[id];
  while (current) {
    chain.unshift(current);
    current = current.parent ? pageById[current.parent] : null;
  }
  return chain;
};

/* -------------------------------------------------------------------------- */
/* Date                                                                        */
/* -------------------------------------------------------------------------- */

/** dd/mm/yyyy, like the original (taken from the stored local timestamp, no timezone shifts). */
export const formatDate = (iso) => {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
};

/* -------------------------------------------------------------------------- */
/* Search                                                                      */
/* -------------------------------------------------------------------------- */

const searchable = [...articleList, ...faqList].map((item) => ({
  item,
  titleLower: item.title.toLowerCase(),
  textLower: item.text.toLowerCase(),
  tagsLower: (item.tags || []).join(' ').toLowerCase(),
  catLower: item.categories
    .flatMap((s) => (item.kind === 'article' ? categoryChain(s) : [{ name: (faqCategories.find((c) => c.slug === s) || {}).name || '' }]))
    .map((c) => c.name)
    .join(' ')
    .toLowerCase(),
  allCats: new Set(item.kind === 'article' ? item.categories.flatMap((s) => categoryChain(s).map((c) => c.slug)) : [])
}));

export const tokenize = (q) => q.toLowerCase().split(/\s+/).filter(Boolean);
export const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const countOccurrences = (haystack, needle) => {
  let count = 0;
  let pos = haystack.indexOf(needle);
  while (pos !== -1 && count < 10) {
    count += 1;
    pos = haystack.indexOf(needle, pos + needle.length);
  }
  return count;
};

export const search = (terms, categoryFilter) => {
  if (!terms.length) return [];
  const phrase = terms.join(' ');
  return searchable
    .filter((s) => !categoryFilter || s.allCats.has(categoryFilter))
    .map((s) => {
      const haystack = `${s.titleLower} ${s.tagsLower} ${s.catLower} ${s.textLower}`;
      if (!terms.every((t) => haystack.includes(t))) return null;
      let score = s.titleLower.includes(phrase) ? 20 : 0;
      terms.forEach((t) => {
        if (s.titleLower.includes(t)) score += 10;
        if (s.tagsLower.includes(t)) score += 5;
        if (s.catLower.includes(t)) score += 3;
        score += countOccurrences(s.textLower, t);
      });
      return { item: s.item, score };
    })
    .filter(Boolean)
    .sort((x, y) => y.score - x.score || byTitle(x.item, y.item))
    .map((r) => r.item);
};

export const makeSnippet = (item, terms) => {
  const lower = item.text.toLowerCase();
  let at = -1;
  terms.forEach((t) => {
    const i = lower.indexOf(t);
    if (i !== -1 && (at === -1 || i < at)) at = i;
  });
  const start = at === -1 ? 0 : Math.max(0, at - 70);
  const slice = item.text.slice(start, start + 220).trim();
  return `${start > 0 ? '…' : ''}${slice}${start + 220 < item.text.length ? '…' : ''}`;
};

/** Flat, depth-first list of KB categories for the search filter. */
export const flatCategories = [];
const walk = (parent, depth) =>
  (childrenOf[parent] || []).forEach((c) => {
    flatCategories.push({ ...c, depth });
    walk(c.slug, depth + 1);
  });
walk('', 0);

/* -------------------------------------------------------------------------- */
/* HTML preparation                                                            */
/* -------------------------------------------------------------------------- */

const BLOCKED_ELEMENTS = 'script, iframe, object, embed, style, link, meta, form, base, frame, frameset';
const URL_ATTRS = ['href', 'src', 'srcset', 'poster', 'action', 'formaction', 'xlink:href'];

// Browsers ignore whitespace / control characters inside a URL scheme ("java\tscript:"), so drop them first
const isScriptUrl = (value) =>
  /^(javascript|vbscript|data):/i.test(
    Array.from(String(value))
      .filter((ch) => ch.charCodeAt(0) > 32)
      .join('')
  );

// "picture_as_pdf" (Material Icons) - replaces the Font Awesome PDF icon of the original
const PDF_ICON_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="1em" height="1em" aria-hidden="true" focusable="false"><path fill="currentColor" d="M20 2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-8.5 7.5c0 .83-.67 1.5-1.5 1.5H9v2H7.5V7H10c.83 0 1.5.67 1.5 1.5v1zm5 2c0 .83-.67 1.5-1.5 1.5h-2.5V7H15c.83 0 1.5.67 1.5 1.5v3zm4-3H19v1h1.5V11H19v2h-1.5V7h3v1.5zM9 9.5h1v-1H9v1zM4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm10 5.5h1v-3h-1v3z"/></svg>';

/**
 * The HTML is static data, but it was exported from a site that was compromised,
 * so it is defensively stripped of anything executable before it is rendered.
 * The only embed that is re-created is a Wistia video, from a validated id.
 */
export const prepareHtml = (html) => {
  if (!html || typeof DOMParser === 'undefined') return html || '';
  const doc = new DOMParser().parseFromString(html, 'text/html');

  doc.querySelectorAll(BLOCKED_ELEMENTS).forEach((el) => el.remove());

  doc.body.querySelectorAll('*').forEach((el) => {
    [...el.attributes].forEach((attr) => {
      const name = attr.name.toLowerCase();
      if (name.startsWith('on')) {
        el.removeAttribute(attr.name);
      } else if (URL_ATTRS.includes(name) && isScriptUrl(attr.value)) {
        el.removeAttribute(attr.name);
      }
    });
  });

  doc.body.querySelectorAll('a[href]').forEach((a) => {
    const href = (a.getAttribute('href') || '').trim();
    const visibleText = (a.textContent || '').replace(/\u00a0/g, ' ').trim();

    // The original shows a PDF icon for these (text-less) links
    if (!visibleText && !a.querySelector('img') && /\.pdf(\?|#|$)/i.test(href)) {
      a.innerHTML = `${PDF_ICON_SVG}<span class="kb-sr-only">Download PDF version</span>`;
      a.classList.add('kb-pdf-link');
      a.setAttribute('title', 'Download PDF version');
    }

    if (/^https?:\/\//i.test(href) || /\.pdf(\?|#|$)/i.test(href)) {
      a.setAttribute('target', '_blank');
      a.setAttribute('rel', 'noopener noreferrer');
    }
  });

  doc.body.querySelectorAll('img').forEach((img) => img.setAttribute('loading', 'lazy'));

  // Wistia videos: rebuilt as an iframe from a strictly validated id
  doc.body.querySelectorAll('.kb-wistia[data-wistia-id]').forEach((el) => {
    const id = el.getAttribute('data-wistia-id') || '';
    if (!/^[a-z0-9]{6,20}$/i.test(id)) return;
    const wrap = doc.createElement('div');
    wrap.className = 'kb-video';
    const frame = doc.createElement('iframe');
    frame.setAttribute('src', `https://fast.wistia.net/embed/iframe/${id}`);
    frame.setAttribute('title', 'Video tutorial');
    frame.setAttribute('allow', 'fullscreen');
    frame.setAttribute('allowfullscreen', '');
    frame.setAttribute('loading', 'lazy');
    wrap.appendChild(frame);
    el.replaceWith(wrap);
  });

  return doc.body.innerHTML;
};
